/**
 * Block 14 «Раскрой» — чистые утилиты таблицы Остатков. Пустая строка, полнота,
 * подсчёт, суммарная площадь, валидация.
 *
 * PR #24 (ADR-0081): таблица Остатков = СКЛАД (`WarehouseOffcut`, cross-project
 * `pomoshnik_offcuts_v1`). Прежняя project-local модель (`ProjectOffcut`)
 * упразднена; per-project хранится только выбор «участвуют» (`participatingOffcutIds`).
 */

import { genUuid } from '@/lib/uuid';
import type { WarehouseOffcut } from './types';

/** Новая пустая строка склада (0×0, qty 1, без имени/материала). */
export function makeEmptyOffcut(): WarehouseOffcut {
  return {
    id: genUuid(), // genUuid: фолбэк для insecure-context/старых WebView (Bug G)
    length: 0,
    width: 0,
    qty: 1,
    name: '',
    material: '',
  };
}

/** Остаток заполнен (имеет положительные размеры) — годен для раскроя. */
export function isCompleteOffcut(o: WarehouseOffcut): boolean {
  return o.length > 0 && o.width > 0;
}

/**
 * Строка остатка в ошибке (row-error, §паттерн parts): задано РОВНО одно из
 * измерений (частично заполнена). Пустая строка-«леса» (0×0) и полная (оба >0) —
 * НЕ ошибка. qty инвариантно ≥1 (коэрсится в setField, как у деталей).
 */
export function offcutRowHasError(o: WarehouseOffcut): boolean {
  return o.length > 0 !== o.width > 0;
}

/** Всего остатков — сумма qty всех строк (футер «Остатков: N шт»). */
export function countOffcuts(offcuts: WarehouseOffcut[]): number {
  return offcuts.reduce((sum, o) => sum + (o.qty || 0), 0);
}

/**
 * Суммарная площадь остатков в м² (футер «Суммарная площадь: X м.кв.»).
 * Σ длина×ширина×кол-во (мм²) / 1e6. Неполные строки (0 размер) дают 0.
 */
export function totalOffcutArea(offcuts: WarehouseOffcut[]): number {
  const mm2 = offcuts.reduce((sum, o) => sum + o.length * o.width * (o.qty || 0), 0);
  return mm2 / 1_000_000;
}
